import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch, type FieldErrors, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPen } from 'lucide-react';
import { composeFullName, countryProfile, registerPatientSchema, type FacilityKind, type RegisterPatientInput } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { activeProfile } from '@/lib/country';
import { FormActions, FormScreen } from '@/components/ui/form-screen';
import { Alert, PageSkeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { missingReasonLabel } from './IdentityDocumentFields';
import { PatientFormSections } from './PatientFormSections';
import { ASIDE_CLASS, CompletenessCard, PatientSummaryCard } from './RegistrationAside';
import { KEEP_PHOTO, draftPreview, useObjectUrlCleanup, type PhotoDraft } from './patient-photo';
import { NO_INSURANCE, runExtra } from './registration-extras';
import { ageSummaryText, categoryLabel, sexText } from './patient-labels';
import {
  ageFacts,
  ageSummary,
  completeness,
  editPayload,
  formValuesOf,
  hasChanges,
  missingRequired,
  sectionProgress,
  validationKeysForEdit,
  type PatientRecord,
  type RegistrationContext,
} from './registration-rules';

function editResolver(initial: RegisterPatientInput): Resolver<RegisterPatientInput> {
  const base = zodResolver(registerPatientSchema);
  return async (values, context, options) => {
    const result = await base(values, context, options);
    const checked = validationKeysForEdit(initial, values);
    const errors = Object.fromEntries(Object.entries(result.errors).filter(([key]) => checked.has(key))) as FieldErrors<RegisterPatientInput>;
    if (Object.keys(errors).length > 0) return { values: {}, errors };
    return { values, errors: {} };
  };
}

export function PatientEditPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const record = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => get<PatientRecord>(`/patients/${patientId}`),
    enabled: Boolean(patientId),
  });

  if (record.isLoading) return <PageSkeleton />;
  if (!record.data) return <Alert variant="destructive">{tr('opd', 'that_patient_record_could_not_be_opened')}</Alert>;
  if (record.data.isDeceased) return <Alert variant="destructive">{tr('opd', 'deceased_record_read_only')}</Alert>;
  return <PatientEditForm record={record.data} />;
}

function PatientEditForm({ record }: { record: PatientRecord }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const facility = (user?.facilityType ?? 'hospital') as FacilityKind;
  const keepsFullRecords = facility === 'hospital';
  const initial = useMemo(() => formValuesOf(record), [record]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<PhotoDraft>(KEEP_PHOTO);
  useObjectUrlCleanup(draftPreview(photo));
  const profilePath = `/patients/${record.id}`;

  const branch = user?.branches.find((candidate) => candidate.id === record.branchId);
  const profile = branch?.country ? countryProfile(branch.country) : activeProfile();

  const resolver = useMemo(() => editResolver(initial), [initial]);
  const form = useForm<RegisterPatientInput>({ resolver, defaultValues: initial });
  const values = useWatch({ control: form.control }) as Partial<RegisterPatientInput>;
  const facts = ageFacts(values);
  const { isMinor, showGuardianDetails } = facts;
  const guardianRelation = values.guardianRelation;
  const context: RegistrationContext = { profile, facility, keepsFullRecords, mode: 'full', needsBranch: false };
  const sections = sectionProgress(values, context, facts);
  const progress = Object.fromEntries(sections.map((section) => [section.id, section.done]));
  const missing = missingRequired(values, context, facts).filter((key) => key !== 'cnic' && key !== 'dob');

  useEffect(() => {
    if (isMinor && guardianRelation === 'self') form.setValue('guardianRelation', 'parent');
  }, [isMinor, guardianRelation, form]);

  useEffect(() => {
    if (!showGuardianDetails) {
      form.setValue('guardianName', '');
      form.setValue('guardianPhone', '');
    }
  }, [showGuardianDetails, form]);

  const save = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const saved: { warnings?: { message: string }[] } = Object.keys(payload).length > 0 ? await put<{ warnings?: { message: string }[] }>(`/patients/${record.id}`, payload) : {};
      const photoResult = photo.kind === 'keep' ? null : await runExtra(record.id, 'photo', { photo, insurance: NO_INSURANCE });
      return { saved, photoResult };
    },
    onSuccess: ({ saved, photoResult }) => {
      void queryClient.invalidateQueries({ queryKey: ['patient', record.id] });
      for (const warning of saved.warnings ?? []) toast.warning(tr('opd', 'please_check'), warning.message);
      if (photoResult && !photoResult.ok) {
        setServerError(tr('opd', 'photo_not_saved_x', { error: photoResult.error ?? '' }));
        return;
      }
      toast.success(tr('opd', 'patient_details_saved'));
      navigate(profilePath, { replace: true });
    },
    onError: (error) => setServerError(describeError(error)),
  });

  const idSummary = values.idType === 'no_document' ? (values.idMissingReason ? missingReasonLabel(values.idMissingReason) : null) : values.cnic;

  return (
    <FormScreen
      title={tr('opd', 'edit_patient_details')}
      description={tr('opd', 'edit_patient_details_hint', { mrn: record.mrn })}
      icon={UserPen}
      wide
      backTo={profilePath}
      dirty={hasChanges(initial, values) || photo.kind !== 'keep'}
      onSubmit={form.handleSubmit((input) => {
        setServerError(null);
        const { payload, problem } = editPayload(initial, input);
        if (problem) {
          setServerError(tr('opd', problem));
          return;
        }
        if (Object.keys(payload).length === 0 && photo.kind === 'keep') {
          navigate(profilePath, { replace: true });
          return;
        }
        save.mutate(payload);
      })}
      aside={
        <div className={ASIDE_CLASS}>
          <PatientSummaryCard
            name={composeFullName(profile, values)}
            sex={sexText(values.sex) ?? ''}
            age={ageSummaryText(ageSummary(values.dob, values.approximateAgeYears))}
            phone={values.phone}
            idNumber={idSummary}
            branch={undefined}
            category={values.category && values.category !== 'general' ? categoryLabel(values.category) : null}
            mrn={record.mrn}
            photoKey={photo.kind === 'keep' ? (record.photoUrl ?? null) : null}
            photoSrc={draftPreview(photo)}
            mode={null}
            isMinor={isMinor}
            infant={facts.infant}
          />
          <CompletenessCard sections={sections} percent={completeness(sections)} keepsFullRecords={keepsFullRecords} />
        </div>
      }
    >
      <PatientFormSections
        form={form}
        purpose="edit"
        profile={profile}
        facility={facility}
        keepsFullRecords={keepsFullRecords}
        mode="full"
        facts={facts}
        progress={progress}
        photo={{ value: photo, onChange: setPhoto, currentKey: record.photoUrl ?? null, name: composeFullName(profile, values) }}
      />

      {serverError && <Alert variant="destructive">{serverError}</Alert>}

      <FormActions submitLabel={tr('opd', 'save_changes')} loading={save.isPending} incomplete={missing.length > 0} cancelTo={profilePath} />
    </FormScreen>
  );
}
